import { useCallback, useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useIsHydrated } from "shared/lib/react/useIsHydrated"

import { isEqual } from "lodash-es"

import { Logger } from "shared/lib/Logger"
import { useEvent } from "shared/lib/useEvent"

import { z } from "zod"

import { deserializeQueryStringArray } from "./deserializeQueryStringArray"
import { serializeQueryStringArray } from "./serializeQueryStringArray"

export interface UseUrlParamsStore<TState> {
  state: TState
  setState: (state: TState) => void
}

function getFieldType(
  fieldSchema: z.ZodTypeAny
): "string" | "array" | "unknown" {
  // Unwrap nullable/optional
  let schema: z.ZodTypeAny = fieldSchema

  while (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) {
    schema = schema.def.innerType as z.ZodTypeAny
  }

  if (schema instanceof z.ZodString) {
    return "string"
  }
  if (schema instanceof z.ZodArray) {
    return "array"
  }
  return "unknown"
}

export function useUrlParamStore<
  TSchema extends z.ZodObject,
  TState extends z.infer<TSchema> = z.infer<TSchema>
>(
  schema: TSchema,
  store: UseUrlParamsStore<TState>
): {
  values: TState
  setValues: (values: TState) => void
} {
  const [searchParams, setSearchParams] = useSearchParams()
  const isHydrated = useIsHydrated()

  // Helper to get empty defaults based on schema
  const emptyDefaults = useMemo(() => {
    return Object.fromEntries(
      Object.keys(schema.shape).map(key => [key, null])
    ) as TState
  }, [schema])

  // Parse URL params based on schema
  const parseFromUrl = useCallback(
    (urlParams: URLSearchParams): TState => {
      return Object.fromEntries(
        Object.entries(schema.shape).map(([key, fieldSchema]) => {
          const rawValue = urlParams.get(key)
          const fieldType = getFieldType(fieldSchema as z.ZodTypeAny)

          if (fieldType === "string") {
            return [key, rawValue]
          }
          if (fieldType === "array") {
            return [key, deserializeQueryStringArray(rawValue)]
          }
          Logger.error("UrlParamsError", "unknown field type", {
            key,
            schema: fieldSchema
          })
          return [key, null]
        })
      ) as TState
    },
    [schema]
  )

  const serializeToUrl = useCallback(
    (state: Partial<TState>): URLSearchParams => {
      const params = new URLSearchParams()

      Object.entries(schema.shape).forEach(([key, fieldSchema]) => {
        const value = state[key]
        const fieldType = getFieldType(fieldSchema as z.ZodTypeAny)

        // Skip null/empty values
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return
        }

        if (fieldType === "string") {
          params.set(key, value as string)
        }
        if (fieldType === "array") {
          params.set(key, serializeQueryStringArray(value as string[]))
        }
      })

      return params
    },
    [schema]
  )

  const urlParams = useMemo(() => {
    const parsed = parseFromUrl(searchParams)

    const validation = schema.safeParse(parsed)
    if (!validation.success) {
      Logger.error(
        "UrlParamsError",
        "an error occured while parsing URL params",
        { errors: validation.error, urlParams: parsed }
      )
      return null
    }
    if (isEqual(parsed, emptyDefaults)) {
      return null
    }

    return parsed
  }, [searchParams, schema, parseFromUrl, emptyDefaults])

  useEffect(() => {
    if (urlParams) {
      store.setState(urlParams)
    } else {
      setSearchParams(serializeToUrl(store.state), { replace: true })
    }
  }, [])

  const setValues = useEvent((newValues: TState) => {
    // Forms mounted during hydration emit their still-empty initial values. Writing
    // those would erase the params we are about to apply in the next render.
    if (!isHydrated) {
      return
    }

    const validation = schema.safeParse(newValues)
    if (!validation.success) {
      Logger.error(
        "UrlParamsError",
        "an error occured while validating URL params",
        { errors: validation.error, data: newValues }
      )
    }

    store.setState(newValues)
    setSearchParams(serializeToUrl(newValues), { replace: true })
  })

  const values = useMemo(() => {
    // The prerendered HTML knows neither URL params nor store state, so the first
    // client render has to stick to the store defaults to stay hydratable.
    if (!isHydrated) {
      return store.state
    }
    if (urlParams && !isEqual(urlParams, store.state)) {
      return urlParams
    }
    return store.state
  }, [isHydrated, urlParams, store.state])

  return { values, setValues }
}
